// src/pages/compliance/CompliancePage.jsx
//
// Relocated from src/compliance/ so this feature matches the self-contained
// page-feature folder the SOW features already use (see pages/rules/ and
// pages/sow-analysis-viewer/) — page, components, utils and stylesheet all
// together, with only the API client kept flat in src/api/. The earlier
// relocation's own content snapshot had dropped the useWorkspace() wiring
// entirely -- _startJob never sent engagement_id/account_id, and
// JobRunHistoryPage never got an engagementId -- restored here, since
// without it jobs can't be scoped to a project and the history tab 422s.
//
// Split into HomeTab.jsx, ToolTab.jsx and utils/compliance/constants.js --
// this file used to hold the tab/nav constants, the whole Home view, and a
// repeated upload/progress/result block per tool tab all in one file. It
// now owns only state, the job-submission handlers, and composition.
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../../api/client'
import { useWorkspace } from '../../context/WorkspaceContext'
import { TAB, TABS, FEATURE_META } from '../../utils/compliance/constants'
import UploadCard from '../../components/compliance/UploadCard'
import RaidUploadCard from '../../components/compliance/RaidUploadCard'
import TvrUploadCard from '../../components/compliance/TvrUploadCard'
import ObligationUploadCard from '../../components/compliance/ObligationUploadCard'
import StmUploadCard from '../../components/compliance/StmUploadCard'
import RtmUploadCard from '../../components/compliance/RtmUploadCard'
import HomeTab from './HomeTab'
import ToolTab from './ToolTab'
import JobRunHistoryPage from './JobRunHistoryPage'
import '../../styles/compliance.css'

const INITIAL_FEATURE_STATE = { phase: 'upload', jobId: null, jobData: null, error: null }

export default function CompliancePage() {
  const { currentEngagementId, currentAccountId, currentEngagement } = useWorkspace()
  // STM (Service Traceability Matrix) only makes sense for a Managed
  // Services engagement; RTM (Requirements Traceability Matrix) only for a
  // T&M or Fixed Bid one -- mirrors the backend's own gate
  // (ModuleType.allowed_contract_types(), enforced in
  // StartComplianceJobHandler regardless of what the frontend shows). No
  // project selected at all -> neither is allowed, same as the backend's
  // "no engagement_id" case.
  const stmAllowed = currentEngagement?.contract_type === 'Managed Services'
  const rtmAllowed = currentEngagement?.contract_type === 'T&M'
    || currentEngagement?.contract_type === 'Fixed Bid'

  // The active tab lives in the URL (/compliance/:tab), not component state,
  // so SMP/RAID/Obligations/etc. are real, linkable, bookmarkable addresses
  // and back/forward move between them -- see AppRoutes.jsx's two /compliance
  // route registrations. An unrecognized or missing :tab (including the bare
  // /compliance route, where :tab is undefined) falls back to Home.
  const { tab: tabParam } = useParams()
  const navigate = useNavigate()
  const validTabKeys = new Set(Object.values(TAB))
  const activeTab = validTabKeys.has(tabParam) ? tabParam : TAB.HOME
  function setActiveTab(key) {
    navigate(key === TAB.HOME ? '/compliance' : `/compliance/${key}`)
  }

  // Falls back to Home if the active tab is STM/RTM but the selected
  // project stopped being eligible (e.g. the user switched projects) --
  // computed during render, since it's a pure derivation of existing
  // state/props, so the page never flashes STM/RTM content it shouldn't.
  const effectiveTab =
    (activeTab === TAB.STM && !stmAllowed) || (activeTab === TAB.RTM && !rtmAllowed)
      ? TAB.HOME
      : activeTab
  // The URL itself only gets corrected as an effect, not during render --
  // changing the route is a sync with something outside this component
  // (the browser's address bar/history), not a derived value. replace:
  // true so an ineligible deep link doesn't leave a dead entry in history.
  useEffect(() => {
    if (activeTab !== effectiveTab) navigate('/compliance', { replace: true })
  }, [activeTab, effectiveTab, navigate])

  const visibleTabs = TABS.filter(({ key }) =>
    (key !== TAB.STM || stmAllowed) && (key !== TAB.RTM || rtmAllowed)
  )
  const [featureState, setFeatureState] = useState({
    [TAB.SMP]:  { ...INITIAL_FEATURE_STATE },
    [TAB.RAID]: { ...INITIAL_FEATURE_STATE },
    [TAB.TVR]:  { ...INITIAL_FEATURE_STATE },
    [TAB.OBLIGATION]: { ...INITIAL_FEATURE_STATE },
    [TAB.STM]:  { ...INITIAL_FEATURE_STATE },
    [TAB.RTM]:  { ...INITIAL_FEATURE_STATE },
  })

  function patchFeature(feature, patch) {
    setFeatureState(prev => ({ ...prev, [feature]: { ...prev[feature], ...patch } }))
  }

  // The SOW picked for whichever module the user runs first — reused by
  // every other SOW-based module so they don't have to upload it again.
  // RTM is deliberately excluded: it takes its own requirements document,
  // not the general SOW (see RtmUploadCard / RtmPipeline).
  const [sharedSowFile, setSharedSowFile] = useState(null)
  const [sharedSowUploadedAt, setSharedSowUploadedAt] = useState(null)

  const rememberSowFile = useCallback((sowFile) => {
    setSharedSowFile(sowFile)
    setSharedSowUploadedAt(Date.now())
  }, [setSharedSowFile, setSharedSowUploadedAt])

  // sowFile is only used to remember it for reuse on other tabs -- passed
  // in separately from formData rather than read back out of it, and only
  // remembered on the SUCCESS path. Remembering it eagerly (as this used to)
  // flips sharedSowFile truthy while phase is still 'upload', which swaps
  // the upload card out for the reuse-path button mid-request -- so a slow
  // first request looked like the click did nothing. RTM never passes a
  // sowFile here, so it's never remembered (see handleRtmSubmit).
  async function _startJob(feature, endpoint, formData, sowFile) {
    if (currentEngagementId) formData.append('engagement_id', currentEngagementId)
    if (currentAccountId) formData.append('account_id', currentAccountId)
    try {
      const { data } = await api.post(`/compliance${endpoint}`, formData)
      // Skip re-remembering when it's the same File already carried forward
      // (the reuse-path button) — otherwise "uploaded Xm ago" would reset to
      // "just now" on every regenerate, even though nothing was re-uploaded.
      if (sowFile && sowFile !== sharedSowFile) rememberSowFile(sowFile)
      patchFeature(feature, { jobId: data.job_id, phase: 'progress' })
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Failed to start the pipeline.'
      patchFeature(feature, { error: detail, phase: 'result' })
    }
  }

  async function handleSmpSubmit(sowFile, templateFile) {
    const fd = new FormData()
    fd.append('sow', sowFile)
    if (templateFile) fd.append('template', templateFile)
    await _startJob(TAB.SMP, '/generate', fd, sowFile)
  }

  async function handleRaidSubmit(sowFile) {
    const fd = new FormData()
    fd.append('sow', sowFile)
    await _startJob(TAB.RAID, '/raid/generate', fd, sowFile)
  }

  async function handleTvrSubmit(sowFile) {
    const fd = new FormData()
    fd.append('sow', sowFile)
    await _startJob(TAB.TVR, '/tvr/generate', fd, sowFile)
  }

  async function handleObligationSubmit(sowFile) {
    const fd = new FormData()
    fd.append('sow', sowFile)
    await _startJob(TAB.OBLIGATION, '/obligation/generate', fd, sowFile)
  }

  async function handleStmSubmit(sowFile) {
    const fd = new FormData()
    fd.append('sow', sowFile)
    await _startJob(TAB.STM, '/stm/generate', fd, sowFile)
  }

  // Not wired to rememberSowFile/SowSourceBar — RTM takes a distinct
  // requirements document, not the general SOW the other modules share.
  // No sowFile passed to _startJob here, so it's never remembered.
  async function handleRtmSubmit(sowFile) {
    const fd = new FormData()
    fd.append('sow', sowFile)
    await _startJob(TAB.RTM, '/rtm/generate', fd)
  }

  function handleComplete(feature, data) { patchFeature(feature, { jobData: data, phase: 'result' }) }
  function handleFail(feature, msg)      { patchFeature(feature, { error: msg,  phase: 'result' }) }
  function handleReset(feature)          { patchFeature(feature, { ...INITIAL_FEATURE_STATE }) }

  // Per-tool config for ToolTab -- the upload component and submit handler
  // for whichever tab is active. onSubmit doubles as the reuse-path handler
  // (ToolTab calls it with the already-uploaded sharedSowFile instead of a
  // freshly-picked one), which is why there's one handler per tab, not two.
  const TOOL_CONFIG = {
    [TAB.SMP]:  { UploadComponent: UploadCard,            onSubmit: handleSmpSubmit,        reuseLabel: 'Generate SMP' },
    [TAB.RAID]: { UploadComponent: RaidUploadCard,        onSubmit: handleRaidSubmit,        reuseLabel: 'Analyse RAID Risks' },
    [TAB.TVR]:  { UploadComponent: TvrUploadCard,         onSubmit: handleTvrSubmit,         reuseLabel: 'Identify Value Opportunities' },
    [TAB.OBLIGATION]: { UploadComponent: ObligationUploadCard, onSubmit: handleObligationSubmit, reuseLabel: 'Extract Obligations' },
    [TAB.STM]:  { UploadComponent: StmUploadCard,         onSubmit: handleStmSubmit,         reuseLabel: 'Generate Service Traceability Matrix' },
    [TAB.RTM]:  { UploadComponent: RtmUploadCard,         onSubmit: handleRtmSubmit,         showSowBar: false },
  }

  const isTool = effectiveTab === TAB.SMP || effectiveTab === TAB.RAID || effectiveTab === TAB.TVR || effectiveTab === TAB.STM || effectiveTab === TAB.RTM || effectiveTab === TAB.OBLIGATION
  const { phase, jobId, jobData, error } = isTool ? featureState[effectiveTab] : {}
  const toolConfig = isTool ? TOOL_CONFIG[effectiveTab] : null

  return (
    <div className="compliance-wrap">
      <nav className="idp-nav">
        {visibleTabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            className={`idp-nav-tab${effectiveTab === key ? ' active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </nav>

      <div className="idp-page">
        {effectiveTab === TAB.HOME    && <HomeTab onNavigate={setActiveTab} stmAllowed={stmAllowed} rtmAllowed={rtmAllowed} />}
        {effectiveTab === TAB.HISTORY && <JobRunHistoryPage engagementId={currentEngagementId} />}

        {isTool && (
          <ToolTab
            tabKey={effectiveTab}
            title={FEATURE_META[effectiveTab].title}
            sub={FEATURE_META[effectiveTab].sub}
            UploadComponent={toolConfig.UploadComponent}
            onSubmit={toolConfig.onSubmit}
            showSowBar={toolConfig.showSowBar !== false}
            sharedSowFile={sharedSowFile}
            sharedSowUploadedAt={sharedSowUploadedAt}
            onReplaceSow={() => setSharedSowFile(null)}
            reuseLabel={toolConfig.reuseLabel}
            onReuseSubmit={() => toolConfig.onSubmit(sharedSowFile)}
            phase={phase}
            jobId={jobId}
            jobData={jobData}
            error={error}
            onComplete={data => handleComplete(effectiveTab, data)}
            onFail={msg => handleFail(effectiveTab, msg)}
            onReset={() => handleReset(effectiveTab)}
          />
        )}
      </div>
    </div>
  )
}
